import { useMemo } from "react";

import { formatDate } from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type CellContext,
    type Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";
import { Link } from "react-router-dom";

import {
    SimpleTestingControl,
    SimpleTestingControlListDto as TestingControlListDto,
} from "api/generated/model";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { DateTimeRangeFilterValue } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeTypes";
import { dateTimeRangeTSTFilterFn } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { TagAndCategoryList } from "components/tag/tagAndCategory";
import { TagList } from "components/tag/tagList";
import { buildTestingControlViewEditUrl } from "org/controlTesting/urlUtils";
import {
    extractTagsAndCategoriesFromIds,
    extractUniqueTagIds,
    formatTagsAndCategoriesString,
} from "org/tags/tagsTSTUtils";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";

import {
    buildTestingControlListTableStateStorageKey,
    TESTING_CONTROL_LIST_COLUMN_IDS as ID,
    testingControlListColumnFiltersCodecMap,
    testingControlListTableDefaultValues,
} from "./consts";
import { createUserNameLookupFnFromResources } from "./utils";

type TestingControlListColumnFilters = ZodCodecMapToType<
    typeof testingControlListColumnFiltersCodecMap
>;

type TestingControlColumnId = (typeof ID)[keyof typeof ID];

interface UseTestingControlListTableProps {
    data: SimpleTestingControl[];
    linkedResources: TestingControlListDto["linkedResources"];
    testingProgramId: number;
}

export function useTestingControlListTable({
    data,
    linkedResources,
    testingProgramId,
}: UseTestingControlListTableProps) {
    const { t } = useTranslation();

    const columns = useMemo(
        () => buildColumns(t, linkedResources),
        [t, linkedResources]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");
    const tableId =
        buildTestingControlListTableStateStorageKey(testingProgramId);
    const stateAndOnChanges = usePresetTableSearchParams(
        tableId,
        testingControlListTableDefaultValues,
        testingControlListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const table = useReactTable({
        state: stateAndOnChanges.state,
        onStateChange: stateAndOnChanges.onStateChange,
        data,
        columns,
        enableRowSelection: true,
        enableSortingRemoval: false,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        defaultColumn: {
            size: TST_COLUMN_SIZE.XXL,
        },
        getCoreRowModel: getCoreRowModel<SimpleTestingControl>(),
        getSortedRowModel: getSortedRowModel<SimpleTestingControl>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<SimpleTestingControl>(),
        getFacetedUniqueValues: getFacetedUniqueValues<SimpleTestingControl>(),
        getFilteredRowModel: getFilteredRowModel<SimpleTestingControl>(),
        getPaginationRowModel: getPaginationRowModel<SimpleTestingControl>(),
    });

    const filterComponent = (
        column: Column<SimpleTestingControl>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, linkedResources);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<SimpleTestingControl>) =>
            column.id === ID.TAGS || column.id === ID.ENTITIES_TO_TEST,
        tableId,
    };
}

// Helper function to create tag cell renderer
const createTagCellRenderer = (
    tagField: "samplingTags" | "tags",
    linkedResources: TestingControlListDto["linkedResources"],
    ComponentType: typeof TagAndCategoryList | typeof TagList
) => {
    return (info: CellContext<SimpleTestingControl, string>) => {
        const tagIds = info.row.original[tagField];
        const { tags, tagCategories } = extractTagsAndCategoriesFromIds(
            tagIds,
            linkedResources.tags,
            linkedResources.tagCategories
        );

        if (ComponentType === TagList) {
            return (
                <TagList
                    mode="HOVER"
                    tags={tags}
                    tagCategories={tagCategories}
                />
            );
        } else {
            return (
                <TagAndCategoryList tags={tags} tagCategories={tagCategories} />
            );
        }
    };
};

const columnHelper = createColumnHelper<SimpleTestingControl>();

const buildColumns = (
    t: TFunction,
    linkedResources: TestingControlListDto["linkedResources"]
) => {
    const userNameLookup = createUserNameLookupFnFromResources(linkedResources);

    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
        }),
        columnHelper.accessor("title", {
            id: ID.NAME,
            enableColumnFilter: false,
            cell: (info) => (
                <Link to={buildTestingControlViewEditUrl(info.row.original.id)}>
                    {info.getValue()}
                </Link>
            ),
            enableSorting: true,
            enableHiding: false,
            sortingFn: "alphanumericCaseSensitive",
            header: () => t("TestingControlList_NameColumn"),
            size: TST_COLUMN_SIZE.L,
            filterFn: "includesString",
        }),
        columnHelper.accessor(
            (row) =>
                formatTagsAndCategoriesString(row.samplingTags, {
                    tags: linkedResources.tags,
                    tagCategories: linkedResources.tagCategories,
                }),
            {
                id: ID.ENTITIES_TO_TEST,
                enableColumnFilter: true,
                cell: createTagCellRenderer(
                    "samplingTags",
                    linkedResources,
                    TagAndCategoryList
                ),
                enableSorting: true,
                sortingFn: (rowA, rowB) => {
                    const aLength = rowA.original.samplingTags.length;
                    const bLength = rowB.original.samplingTags.length;
                    return aLength - bLength;
                },
                header: () => t("TestingControlList_EntitiesToTestColumn"),
                size: TST_COLUMN_SIZE.L,
                filterFn: createFilterFnWithMode<
                    SimpleTestingControl,
                    string[]
                >((row, value) => {
                    const rowTagIds = row.original.samplingTags;
                    return value.some((tagId) => rowTagIds.includes(tagId));
                }),
            }
        ),
        columnHelper.accessor(
            (row) =>
                formatTagsAndCategoriesString(row.tags, {
                    tags: linkedResources.tags,
                    tagCategories: linkedResources.tagCategories,
                }),
            {
                id: ID.TAGS,
                enableColumnFilter: true,
                cell: createTagCellRenderer("tags", linkedResources, TagList),
                enableSorting: true,
                sortingFn: (rowA, rowB) => {
                    const aLength = rowA.original.tags.length;
                    const bLength = rowB.original.tags.length;
                    return aLength - bLength;
                },
                header: () => t("TestingControlList_TagsColumn"),
                size: TST_COLUMN_SIZE.L,
                filterFn: createFilterFnWithMode<
                    SimpleTestingControl,
                    string[]
                >((row, value) => {
                    const rowTagIds = row.original.tags;
                    return value.some((tagId) => rowTagIds.includes(tagId));
                }),
            }
        ),
        columnHelper.accessor("responsibleUserId", {
            id: ID.ASSIGNED_TO,
            enableColumnFilter: true,
            cell: (info) => {
                return <div>{userNameLookup(info.getValue())}</div>;
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const aName = userNameLookup(rowA.original.responsibleUserId);
                const bName = userNameLookup(rowB.original.responsibleUserId);
                return aName.localeCompare(bName);
            },
            header: () => t("TestingControlList_AssignedToColumn"),
            size: TST_COLUMN_SIZE.L,
            filterFn: createFilterFnWithMode<SimpleTestingControl, number[]>(
                (row, value) => value.includes(row.original.responsibleUserId)
            ),
        }),
        columnHelper.accessor("dueDateUtc", {
            id: ID.DUE_DATE,
            enableColumnFilter: true,
            enableGlobalFilter: false,
            enableSorting: true,
            cell: (info) => {
                const value = info.getValue();
                return value ? formatDate(value) : "";
            },
            sortingFn: "datetime",
            header: () => t("TestingControlList_DueDateColumn"),
            size: TST_COLUMN_SIZE.S,
            filterFn: createFilterFnWithMode<
                SimpleTestingControl,
                DateTimeRangeFilterValue
            >((row, value) => {
                const dueDate = row.original.dueDateUtc;
                return dueDate
                    ? dateTimeRangeTSTFilterFn(value, dueDate)
                    : false;
            }),
        }),
    ];
};

const getFilterComponent = (
    t: TFunction,
    column: Column<SimpleTestingControl>,
    target: FilterTarget,
    linkedResources: TestingControlListDto["linkedResources"]
) => {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingControlListColumnFilters>(column);
    const columnId = column.id as TestingControlColumnId;
    const userNameLookup = createUserNameLookupFnFromResources(linkedResources);

    switch (columnId) {
        case ID.ENTITIES_TO_TEST: {
            const rows = column.getFacetedRowModel();
            const uniqueTagIds = extractUniqueTagIds(
                rows.flatRows.map((row) => row.original),
                (row) => row.samplingTags
            );
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={linkedResources.tagCategories}
                    tags={linkedResources.tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t(
                        "TestingControlList_EntitiesToTestFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case ID.TAGS: {
            const rows = column.getFacetedRowModel();
            const uniqueTagIds = extractUniqueTagIds(
                rows.flatRows.map((row) => row.original),
                (row) => row.tags
            );
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={linkedResources.tagCategories}
                    tags={linkedResources.tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t("TestingControlList_TagsFilterPlaceholder")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case ID.ASSIGNED_TO: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={linkedResources.users.map((user) => ({
                        label: userNameLookup(user.id),
                        value: user.id,
                    }))}
                    placeholder={t(
                        "TestingControlList_FilterAssignedToPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.DUE_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarDateTimeRange
                    filterValue={value}
                    setFilterValue={setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        case ID.NAME:
            throw new Error("Name column should not be filterable");
        default:
            return assertNever(columnId);
    }
};
